/* ====================================================================
 *  catalogFilter — фільтрація, сортування і пагінація карток каталогу.
 *  Pure: без Vue, без Firestore, без h3. REFACTOR_PLAN п.9а.
 *
 *  Живе окремо від gridItems навмисно: там «як товар стає картками»,
 *  тут «які з карток бачить користувач і в якому порядку». Спільний
 *  код виконується і на сервері (/api/catalog), і в браузері — як
 *  pricing.mjs у грошах: одна реалізація, тож видача детермінантна.
 *
 *  ПАГІНАЦІЯ РАХУЄТЬСЯ В КАРТКАХ, А НЕ В ТОВАРАХ. До 9а Firestore
 *  віддавав 12 ТОВАРІВ на сторінку, вони розгортались у ~60 карток, і
 *  всі показувались одразу. Одиниця мусить бути одна, бо саме від неї
 *  залежить, скільки різних товарів потрапляє в серверний HTML — тобто
 *  вся мета пункту. Порядок наскрізний лише тому, що сортується ПОВНА
 *  вибірка, а ріжеться вже відсортоване.
 *
 *  Стеля рішення: одна вибірка всієї опублікованої колекції на кеш-міс.
 *  На поточних даних (12 товарів / ~60 варіантів) це дешевше за будь-яку
 *  денормалізацію. Коли каталог виросте до сотень товарів — тримати ранг
 *  і ціну варіанта в документі товару й сортувати вже у Firestore.
 * ==================================================================== */

import {
  isExclusiveVariant,
  isVariantValid,
  readAvailability,
} from "~/composables/product/variantDisplay";
import {
  readCreatedAtMs,
  sortGridItems,
  type GridItem,
  type GridSortMode,
} from "~/composables/catalog/gridItems";

/** Режими сортування, доступні користувачу (без службового keep-order). */
export const CATALOG_SORTS = ["new", "price-asc", "price-desc"] as const;
export type CatalogSort = (typeof CATALOG_SORTS)[number];

/**
 * Скільки КАРТОК віддається за сторінку.
 *
 * Число заміряне, а не вигадане. «Показати ще» — кнопка, а не посилання,
 * тож усе за межами першої сторінки краулер не побачить у принципі;
 * значить перша сторінка мусить містити кожен опублікований товар
 * хоча б раз. Заміряно на /api/catalog (2026-08-21, 12 товарів / 71
 * картка):
 *
 *     limit=12 → 5/12 товарів      limit=48 → 11/12
 *     limit=24 → 7/12              limit=60 → 12/12  ← беремо
 *     limit=36 → 9/12
 *
 * До 9а сторінка показувала всі 71 картку одразу (Firestore віддавав
 * 12 ТОВАРІВ, вони розгортались у картки), тож 60 — це трохи менше DOM,
 * а не менше товарів.
 *
 * Коли каталог виросте, правило «перша сторінка покриває все» перестане
 * бути досяжним: тоді відкриття товарів тримається на sitemap, а
 * «Показати ще» треба міняти на пагінацію посиланнями (?page=2), яку
 * краулер уміє ходити. Перевірка покриття — scripts/seo-audit.mjs,
 * колонка «товарів».
 */
export const CATALOG_PAGE_SIZE = 60;

/** Скільки slug'ів приймаємо з URL — захист від сміття в query. */
const MAX_CATS = 30;

export interface CatalogFilters {
  cats: string[];
  exclusive: boolean;
  sort: CatalogSort;
}

export const DEFAULT_FILTERS: CatalogFilters = {
  cats: [],
  exclusive: false,
  sort: "new",
};

/* ---------- URL ↔ стан ---------- */

function readCats(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw.map((v) => String(v ?? ""))
    : String(raw ?? "").split(",");
  const cleaned = list.map((s) => s.trim()).filter(Boolean);
  return [...new Set(cleaned)].slice(0, MAX_CATS);
}

/**
 * URL → стан фільтрів.
 *
 * Після 9а `route.query` — ЄДИНЕ джерело правди сторінки: стан
 * обчислюється з нього, а не синхронізується з ним. До 9а тут стояли
 * два зустрічні watcher'и (стан→URL і URL→стан), і саме вони були
 * найкрихкішим місцем сторінки.
 */
export function parseCatalogQuery(
  query: Record<string, unknown> = {},
): CatalogFilters {
  const sort = String(query.sort ?? "");
  return {
    cats: readCats(query.cat),
    exclusive: String(query.ex ?? "") === "1",
    sort: (CATALOG_SORTS as readonly string[]).includes(sort)
      ? (sort as CatalogSort)
      : "new",
  };
}

/**
 * Стан → URL. Дефолти НЕ пишуться в query, щоб `/catalog` лишався
 * чистим (canonical у нас без query, і зайві параметри плодили б
 * дублікати в індексі).
 */
export function toCatalogQuery(
  filters: CatalogFilters,
): Record<string, string> {
  const q: Record<string, string> = {};
  if (filters.cats.length) q.cat = filters.cats.join(",");
  if (filters.sort && filters.sort !== "new") q.sort = filters.sort;
  if (filters.exclusive) q.ex = "1";
  return q;
}

/* ---------- відбір ---------- */

/**
 * Страховка на рівні картки: товар могли зняти з публікації або
 * сховати вже після вибірки, а варіант — зіпсувати. Було на сторінці
 * до 9а, лишається тут.
 */
export function isDisplayableItem(item: GridItem): boolean {
  const p = item?.product as Record<string, unknown> | undefined;
  if (!p) return false;

  // `published` мусить бути ІСТИННИМ, а не просто «не false»: відсутнє
  // поле означає «не показувати». Різниця не косметична — на сторінці
  // наволочок стоїть м'якша перевірка (`!== false`), і якщо перенести
  // сюди її, товари без поля стали б видимими в каталозі вперше за весь
  // час. Спіймано diff-харнесом проти старої visibleFlat (969 розбіжностей
  // на 1800 порівнянь), очима таке не видно.
  if (!p.published) return false;
  if (p.hidden) return false;
  if (item.variant && !isVariantValid(item.product, item.variant)) return false;
  return true;
}

/**
 * Фільтр за вибором користувача. Логіка АБО, не І: картка підходить,
 * якщо вона в обраній категорії АБО ексклюзивна. Так було до 9а —
 * «Ексклюзив» розширює вибірку, а не звужує її.
 */
function matchesFilters(item: GridItem, filters: CatalogFilters): boolean {
  const hasCats = filters.cats.length > 0;
  if (!hasCats && !filters.exclusive) return true;

  // Свідомо БЕЗ зведення до рядка: `?? ""` зробило б так, що товар без
  // categorySlug підпадає під фільтр порожнього слага (`?cat=`), тобто
  // товари без категорії стали б видимими там, де їх ніколи не було.
  // Стара сторінка порівнювала сире значення — так і лишаємо.
  // Той самий клас помилки, що регресія isVariantValid у п.9.
  const categorySlug = (item.product as Record<string, unknown>)?.categorySlug;
  const byCat = hasCats && filters.cats.includes(categorySlug as string);
  const byExclusive = filters.exclusive && isExclusiveVariant(item.variant);
  return byCat || byExclusive;
}

export interface SelectPageOptions extends Partial<CatalogFilters> {
  offset?: number;
  /** Розмір сторінки В КАРТКАХ. `undefined` — віддати все. */
  limit?: number;
}

/**
 * Стеля, до якої клієнт має право фільтрувати САМ.
 *
 * Поки весь каталог влазить у цю кількість карток, сторінка тягне його
 * один раз цілком і рахує фільтри локально тією ж `selectCatalogPage` —
 * клік стає миттєвим замість ~1 с мережевого очікування (заміряно на
 * проді: 1.22 с очікування відповіді на клік по категорії).
 *
 * Вище стелі так робити НЕ МОЖНА: у клієнта буде обрізана вибірка, і
 * локальна фільтрація по ній тихо брехала б. Тому `complete` у відповіді
 * — не оптимізація, а запобіжник, і на нього є тест.
 *
 * 200 — з запасом до поточних 71 картки. Ціна повного набору в payload
 * заміряна: 108 КБ проти 90 КБ на сторінку в 60 карток.
 */
export const CATALOG_COMPLETE_LIMIT = 200;

export interface CatalogPage {
  items: GridItem[];
  /** Скільки карток підходить під фільтри ВСЬОГО, не лише на сторінці. */
  total: number;
  hasMore: boolean;
  /**
   * У `items` лежить ВСЯ вибірка, що підходить під запит: нічого не
   * відрізано ні зверху (`offset`), ні знизу (`limit`). Тільки за цієї
   * умови викликач має право фільтрувати далі самотужки.
   */
  complete: boolean;
}

/**
 * Повний конвеєр: страховка → фільтри → сортування → зріз.
 *
 * Порядок критичний. Сортування йде по ПОВНІЙ відфільтрованій вибірці,
 * і лише потім береться зріз — саме тому порядок наскрізний між
 * сторінками. До 9а сортувалась кожна догружена сторінка окремо, тож
 * «ціна: зростання» між сторінками не була наскрізною.
 */
export function selectCatalogPage(
  items: GridItem[],
  opts: SelectPageOptions = {},
): CatalogPage {
  const filters: CatalogFilters = {
    cats: opts.cats ?? DEFAULT_FILTERS.cats,
    exclusive: opts.exclusive ?? DEFAULT_FILTERS.exclusive,
    sort: opts.sort ?? DEFAULT_FILTERS.sort,
  };

  const matched = (Array.isArray(items) ? items : [])
    .filter(isDisplayableItem)
    .filter((it) => matchesFilters(it, filters));

  const sorted = sortGridItems(matched, filters.sort as GridSortMode);

  const offset = Math.max(0, Number(opts.offset) || 0);
  const limit =
    typeof opts.limit === "number" && opts.limit >= 0
      ? opts.limit
      : sorted.length;
  const page = sorted.slice(offset, offset + limit);

  return {
    items: page,
    total: sorted.length,
    hasMore: offset + page.length < sorted.length,
    complete: offset === 0 && page.length === sorted.length,
  };
}

/* ---------- новинки на головній ---------- */

/**
 * Скільки карток показує блок «Новинки». REFACTOR_PLAN п.9б.
 *
 * Рахується в ТОВАРАХ, а не в картках — після дедуплікації це одне й те
 * саме. У цьому вся різниця з `CATALOG_PAGE_SIZE`: там 60 карток дають
 * 12 товарів, тут 8 карток дають рівно 8 товарів.
 *
 * 8 із 12 опублікованих — свідомо велика частка. Коли асортимент
 * виросте, до числа варто повернутись: блок називається «Новинки», а не
 * «Вітрина», і показувати ним дві третини каталогу довго не можна.
 */
export const NEW_ARRIVALS_SIZE = 8;

/**
 * Картки блока «Новинки» на головній. REFACTOR_PLAN п.9б.
 *
 * Конвеєр НЕ той самий, що в `selectCatalogPage`, і всі три відмінності
 * змістовні — це не спрощення, а інша задача.
 *
 * 1. ОДНА КАРТКА НА ТОВАР. Без дедуплікації блок на 8 карток заміряно
 *    показував рівно ДВА товари: чотири кольори сукні і чотири кольори
 *    чепчика (прод, 2026-08-21). Для сторінки, чия мета — вивести товари
 *    в серверний HTML, це поразка; для вітрини на головній — теж.
 *
 * 2. БЕЗ `gridRank`. Каталог головним ключем тримає ранг (наявність,
 *    потім ексклюзивність), і `sortGridItems` тут дав би те саме, бо
 *    сьогодні серед найновіших ексклюзивів немає — але щойно вони
 *    з'являться, ексклюзивна новинка поїде В КІНЕЦЬ блока. Блок
 *    називається «Новинки», тож ключ один: дата. На це є тест, бо на
 *    живих даних різницю зараз не побачити.
 *
 * 3. ТІЛЬКИ НАЯВНЕ, і без порожніх карток. `expandLiteToItems` товару
 *    без жодного валідного варіанта віддає одну картку з `variant: null`
 *    — у каталозі це правильно (товар видно як розпроданий), у новинках
 *    ні. Наявність перевіряється по ВАРІАНТУ строго (`=== true`), без
 *    фолбеку на товар: `isItemInStock` за відсутності сигналів вважає
 *    товар наявним, а тут відсутність сигналу — привід не показувати.
 */
export function selectNewArrivals(
  items: GridItem[],
  opts: { limit?: number } = {},
): CatalogPage {
  const available = (Array.isArray(items) ? items : [])
    .filter(isDisplayableItem)
    .filter((it) => readAvailability(it.variant) === true);

  // Дедуплікація: лишається ПЕРША картка товару у вхідному порядку. Це
  // зберігає вибір представника, який був до 9б: `variantsLite`
  // збирається обходом сабколекції в порядку doc id, і старий прямий
  // `getDocs(collection(...))` на головній читав так само.
  //
  // Товар без `id` відкидається, а не показується: без нього немає ні
  // ключа дедуплікації, ні робочого посилання — картка веде на
  // `/product/${slug || id}`.
  const seen = new Set<string>();
  const perProduct: GridItem[] = [];
  for (const it of available) {
    const pid = String((it.product as Record<string, unknown>)?.id ?? "");
    if (!pid || seen.has(pid)) continue;
    seen.add(pid);
    perProduct.push(it);
  }

  // `readCreatedAtMs`, а не порівняння полів: з /api/catalog `createdAt`
  // приходить ISO-РЯДКОМ (див. tsToIso), а з клієнтського SDK — це
  // Timestamp. Тай-брейк по вхідному індексу тримає сортування стабільним.
  const sorted = perProduct
    .map((it, i) => ({ it, i, createdAt: readCreatedAtMs(it.product) }))
    .sort((a, b) => b.createdAt - a.createdAt || a.i - b.i)
    .map((r) => r.it);

  const limit =
    typeof opts.limit === "number" && opts.limit >= 0
      ? opts.limit
      : sorted.length;
  const page = sorted.slice(0, limit);

  return {
    items: page,
    total: sorted.length,
    hasMore: page.length < sorted.length,
    complete: page.length === sorted.length,
  };
}
